import { useEffect, useState } from 'react';

export type Urgency = 'closed' | 'critical' | 'soon' | 'later';

/** < 1h is critical, < 24h is soon. Drives card colour, not motion. */
export function urgency(cutoffSec: number | null, nowMs: number): Urgency {
  if (cutoffSec === null) return 'later';
  const left = cutoffSec * 1000 - nowMs;
  if (left <= 0) return 'closed';
  if (left < 3_600_000) return 'critical';
  if (left < 86_400_000) return 'soon';
  return 'later';
}

/** "02:14:09" under a day, "1d 03h" under a week, else "Sep 30". */
export function countdown(cutoffSec: number | null, nowMs: number): string {
  if (cutoffSec === null) return 'no close';
  const left = Math.floor((cutoffSec * 1000 - nowMs) / 1000);
  if (left <= 0) return 'closed';
  const d = Math.floor(left / 86400);
  const h = Math.floor((left % 86400) / 3600);
  const m = Math.floor((left % 3600) / 60);
  const s = left % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (d === 0) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  if (d < 7) return `${d}d ${pad(h)}h`;
  return new Date(cutoffSec * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** A shared ticking clock. One interval per consumer; pass a coarse interval
 *  when nothing on screen shows seconds. Pauses while the tab is hidden. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let id: ReturnType<typeof setInterval> | undefined;
    const start = () => {
      setNow(Date.now());
      id = setInterval(() => setNow(Date.now()), intervalMs);
    };
    const onVis = () => {
      clearInterval(id);
      if (!document.hidden) start();
    };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [intervalMs]);
  return now;
}
